import {html, HTMLTemplateResult, nothing} from "lit";
import {repeat} from "lit/directives/repeat.js";
import type {TimelineToolbarItem} from "../types/timeline-toolbar-item.interface";

export class ToolbarHelper {

    /**
     * Renders the timeline toolbar with provided toolbar items.
     *
     * This method is used by AbstractO2dTimelineView-based views. Items are
     * grouped by position (start/end) and sorted by order within each group.
     * Each item's render() method is wrapped in an error boundary.
     *
     * @param {TimelineToolbarItem[]} items - An array of timeline toolbar items to render.
     * @return {HTMLTemplateResult} The rendered HTML template for the timeline toolbar.
     *
     * @remarks
     * Differences from renderMainToolbar (legacy):
     * - Uses TimelineToolbarItem interface (self-contained, no CommandBus param)
     * - Position values are 'start' | 'end' (logical, not 'left' | 'right')
     * - Items sorted by order property (default 0, stable sort)
     * - Error boundary: catches render() exceptions and renders nothing
     * - Synchronous render (no until() directive needed)
     *
     * @example
     * ```ts
     * const toolbar = ToolbarHelper.renderTimelineToolbar([
     *     resetToTodayToolbarItem,
     *     addEntityMenuToolbarItem
     * ]);
     * ```
     */
    static renderTimelineToolbar(
        items: TimelineToolbarItem[]
    ): HTMLTemplateResult {

        /**
         * Safely renders a single toolbar item with error boundary.
         * On error: renders nothing and logs to console.
         */
        const safeRender = (item: TimelineToolbarItem) => {
            try {
                return item.render();
            } catch (error) {
                console.error(`Toolbar item "${item.id}" failed to render:`, error);
                return nothing;
            }
        };

        /**
         * Sorts items by order property (default 0).
         * Stable sort preserves array order for items with equal order.
         */
        const sortByOrder = (a: TimelineToolbarItem, b: TimelineToolbarItem) => {
            const orderA = a.order ?? 0;
            const orderB = b.order ?? 0;
            return orderA - orderB;
        };

        // Group items by position and sort by order
        const startItems = items
            .filter(item => item.position === 'start')
            .sort(sortByOrder);
        const endItems = items
            .filter(item => item.position === 'end')
            .sort(sortByOrder);

        return html`
            <div class="timeline-toolbar">
                <div class="timeline-toolbar-start">
                    ${repeat(startItems, item => item.id, item => safeRender(item))}
                </div>
                <div class="timeline-toolbar-end">
                    ${repeat(endItems, item => item.id, item => safeRender(item))}
                </div>
            </div>
        `;
    }
}